import { computed, ref, unref } from 'vue';
import SalesTransactionPartiesAPI from 'dashboard/api/salesTransactionParties';
import { isValidCpf } from './commissionPaymentValidation';

const digits = value => String(value || '').replace(/\D/g, '');
const mergeProfile = (current, found) => ({
  ...current,
  identity: { ...(current.identity || {}), ...(found.identity || {}) },
  address: { ...(current.address || {}), ...(found.address || {}) },
});

export function useSalesPartyLookup({ transactionId, profile }) {
  const loading = ref(false);
  const spouseLoading = ref(false);
  const internalProfile = ref(false);
  const spouseInternalProfile = ref(false);
  const lookupError = ref('');
  const spouseLookupError = ref('');
  let lastCpf = '';
  let lastResult = { error: '', profile: null, internalProfile: false };

  const find = async (value, loadingState, errorState) => {
    const cpf = digits(value);
    if (cpf.length !== 11) {
      lastCpf = '';
      lastResult = { error: '', profile: null, internalProfile: false };
      errorState.value = '';
      return null;
    }
    if (cpf === lastCpf) {
      errorState.value = lastResult.error;
      return lastResult;
    }
    lastCpf = cpf;

    loadingState.value = true;
    try {
      const response = await SalesTransactionPartiesAPI.lookup(
        unref(transactionId),
        cpf
      );
      const result = response?.data?.data || {};
      lastResult = {
        error: result.error || '',
        profile: result.profile || null,
        internalProfile: Boolean(result.internal_profile),
      };
      errorState.value = lastResult.error;
      return lastResult;
    } catch {
      lastResult = { error: '', profile: null, internalProfile: false };
      errorState.value = '';
      return null;
    } finally {
      loadingState.value = false;
    }
  };

  const lookup = async value => {
    const cpf = digits(value);
    if (cpf.length !== 11) internalProfile.value = false;
    const result = await find(value, loading, lookupError);
    if (!result || digits(profile.value.identity?.cpf) !== cpf) {
      internalProfile.value = false;
      return null;
    }

    internalProfile.value = result.internalProfile;
    const found = result.profile;
    if (!found) return null;

    profile.value = mergeProfile(profile.value, found);
    return found;
  };

  const lookupSpouse = async value => {
    if (!isValidCpf(value)) {
      spouseInternalProfile.value = false;
      spouseLookupError.value = '';
      return null;
    }
    const cpf = digits(value);
    const result = await find(value, spouseLoading, spouseLookupError);
    if (!result || digits(profile.value.identity?.spouse_cpf) !== cpf) {
      spouseInternalProfile.value = false;
      return null;
    }

    spouseInternalProfile.value = result.internalProfile;
    const found = result.profile;
    if (!found) return null;

    const identity = found.identity || {};
    profile.value = {
      ...profile.value,
      identity: {
        ...profile.value.identity,
        spouse_name: identity.name || '',
        spouse_birth_date: identity.birth_date || '',
        spouse_gender: identity.gender || '',
        spouse_mother_name: identity.mother_name || '',
      },
    };
    return found;
  };

  return {
    loading,
    lookup,
    lookupError,
    lookupSpouse,
    spouseLoading,
    spouseLookupError,
    internalProfile,
    internalProfileAlert: computed(
      () => internalProfile.value || spouseInternalProfile.value
    ),
  };
}
